/* SOHNA owns the cart UI; Monster V1 remains the discount/checkout engine.
 * Selectors are documented by Monster. Do not disable its app embed while this
 * adapter is enabled. An unavailable/out-of-sync engine must never silently
 * send a customer to checkout without their quantity offer.
 */
(() => {
  // Launch suspension uses Shopify automatic discounts and native checkout.
  if (window.sohnaQuantityOffersSuspended) return;
  if (window.sohnaCartEngine) return;
  window.sohnaCartEngine = true;
  const nativeButton = '.cart-summary__checkout-button';
  let scheduled = false;
  let checkingOut = false;
  let cart = null;
  let refreshId = 0;
  // Same one-time quantity tiers as the verified product options and campaign.
  // Use original prices: Shopify retains the previous generated discount after
  // returning from checkout, which makes Monster V1's displayed total stale.
  const offerVariants = new Set([53320645607691, 53320657731851, 53320662647051,
    53320667889931, 53322872160523, 53322872193291, 53322879271179]);
  function hasCartDiscount() {
    return cart.cart_level_discount_applications?.some(discount => discount.total_allocated_amount > 0);
  }
  function offerTotal() {
    // Preserve a shopper's applied order-level code when returning from checkout.
    // Monster retains that code, so displaying the volume tier here would disagree
    // with checkout (e.g. one EUR100 pack with BYSOHNA10 must display EUR90).
    if (hasCartDiscount()) return cart.total_price;
    return cart.items.reduce((sum, item) => {
      const eligible = offerVariants.has(item.variant_id) && !item.selling_plan_allocation;
      const rate = item.quantity >= 3 ? 15 : item.quantity === 2 ? 10 : 0;
      return sum + (eligible ? Math.round(item.original_line_price * (100 - rate) / 100) : item.final_line_price);
    }, 0);
  }
  async function refresh() {
    const id = ++refreshId;
    cart = null;
    schedule();
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      const current = await response.json();
      if (id !== refreshId) return;
      cart = current;
      if (typeof window.monster_refresh === 'function') window.monster_refresh(false, schedule);
    } catch { /* Keep checkout disabled with the visible retry message. */ }
    schedule();
  }
  let resetCheckout;
  const pending = new WeakMap();
  const text = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const signature = rows => rows.map(([id, quantity]) => `${id}:${quantity}`).sort().join('|');
  function engine() {
    const button = document.querySelector('#mu-checkout-button');
    const rows = [...document.querySelectorAll('#monster-upsell-cart .mu-cart-item')];
    return { button, rows, signature: signature(rows.map(row => [row.dataset.variantId, row.querySelector('input[type="number"]')?.value])), total: button?.dataset.display };
  }
  function nativeSignature(summary) {
    const container = summary.closest('cart-items-component') || summary.closest('.shopify-section');
    return signature([...container.querySelectorAll('[data-key][data-line]')].map(row => [row.dataset.key.split(':')[0], row.querySelector('input[name="updates[]"]')?.value]));
  }
  function ready(summary, state) {
    return cart && signature(cart.items.map(item => [item.variant_id, item.quantity])) === state.signature
      && state.button && state.total && !state.button.disabled && state.button.dataset.loading !== 'true'
      && state.rows.length && nativeSignature(summary) === state.signature
      && !document.querySelector('cart-items-component.cart-items-disabled');
  }
  function sync() {
    scheduled = false;
    const root = document.querySelector('#monster-upsell-cart');
    if (root) {
      if (root.getAttribute('aria-hidden') !== 'true') root.setAttribute('aria-hidden', 'true');
      // Close through the app's own control to release its scroll lock.
      if (root.querySelector('#monster-cart-wrapper.mu_openned')) {
        if (typeof window.closeCart === 'function') window.closeCart();
        else root.querySelector('#mu-header [data-icon="times"]')?.parentElement.click();
      }
    }
    const state = engine();
    for (const summary of document.querySelectorAll('.cart-summary')) {
      const button = summary.querySelector(nativeButton);
      if (!button) continue;
      const isReady = ready(summary, state);
      const disabled = !isReady || checkingOut;
      if (button.disabled !== disabled) button.disabled = disabled;
      let notice = summary.querySelector('[data-sohna-cart-status]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.sohnaCartStatus = '';
        notice.setAttribute('role', 'status');
        notice.className = 'text-body-sm';
        (summary.querySelector('.cart-summary__total') || button.parentElement).append(notice);
      }
      if (isReady) {
        pending.delete(summary);
        const discounted = cart.items.some(item => offerVariants.has(item.variant_id) && !item.selling_plan_allocation && item.quantity >= 2);
        const displayTotal = new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: cart.currency }).format(offerTotal() / 100);
        text(notice, checkingOut ? 'Redirection vers le paiement…' : hasCartDiscount() ? 'Le total inclut la remise déjà appliquée à votre panier.' : discounted ? 'Votre réduction quantité est incluse dans le total.' : '');
        const total = summary.querySelector('[data-cart-subtotal]');
        text(total?.querySelector('.price'), displayTotal);
        if (total && total.getAttribute('value') !== displayTotal) total.setAttribute('value', displayTotal);
      } else {
        if (!pending.has(summary)) {
          pending.set(summary, Date.now());
          setTimeout(schedule, 10000);
        }
        text(notice, checkingOut ? 'Redirection vers le paiement…' : Date.now() - pending.get(summary) >= 9500
          ? 'Le panier n’a pas pu être actualisé. Actualisez la page pour réessayer.'
          : 'Mise à jour du panier…');
      }
    }
  }
  function schedule() {
    if (!scheduled) { scheduled = true; setTimeout(sync, 0); }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest(nativeButton);
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const state = engine();
    if (checkingOut || !ready(button.closest('.cart-summary'), state)) return;
    checkingOut = true;
    sync();
    state.button.click();
    clearTimeout(resetCheckout);
    resetCheckout = setTimeout(() => { checkingOut = false; sync(); }, 15000);
  }, true);
  document.addEventListener('submit', event => {
    if (!event.submitter?.matches(nativeButton)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    event.submitter.click();
  }, true);
  window.addEventListener('pageshow', () => { checkingOut = false; refresh(); });
  document.addEventListener('cart:update', refresh);
  document.addEventListener('cart:updated', schedule);
  new MutationObserver(schedule).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'data-display', 'data-loading', 'value', 'disabled'] });
  refresh();
})();
